import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TableColumn } from '../../types/domain';
import { Button } from '../ui/Button';
import {
  FileCode,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ArrowUpFromLine,
  Sparkles,
} from 'lucide-react';

export interface AdvancedJsonSchemaProps {
  columns: TableColumn[];
  canEdit: boolean;
  onApplyColumns?: (newColumns: TableColumn[]) => void;
}

export interface SchemaError {
  path: string;
  message: string;
}

export interface JsonValidationResult {
  isValid: boolean;
  isSyntaxError: boolean;
  syntaxError?: string;
  line?: number;
  column?: number;
  schemaErrors: SchemaError[];
  fieldCount?: number;
}

interface JsonToken {
  text: string;
  type: 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'plain';
}

const VALID_TOP_LEVEL_TYPES = new Set([
  'text',
  'number',
  'boolean',
  'email',
  'date',
  'uuid',
  'object',
  'array',
  'file',
]);

const VALID_SUBFIELD_TYPES = new Set([
  'text',
  'number',
  'boolean',
  'email',
  'date',
  'uuid',
  'file',
]);

/**
 * Serializes TableColumn[] into an ergonomic, developer-friendly JSON schema representation
 */
export function serializeColumnsToJson(columns: TableColumn[]): string {
  const simplified = columns.map((col) => {
    const item: Record<string, any> = {
      key: col.key,
      name: col.name,
      data_type: col.data_type,
      is_required: Boolean(col.is_required),
    };

    if (col.validation_rules?.sample_value) {
      item.sample_value = col.validation_rules.sample_value;
    }

    if (col.data_type === 'array' && col.validation_rules?.max_items) {
      item.max_items = col.validation_rules.max_items;
    }

    if (col.validation_rules?.subfields && col.validation_rules.subfields.length > 0) {
      item.subfields = col.validation_rules.subfields.map((sub) => {
        const subItem: Record<string, any> = {
          key: sub.key,
          name: sub.name,
          data_type: sub.data_type,
          is_required: Boolean(sub.is_required),
        };
        if (sub.sample_value) {
          subItem.sample_value = sub.sample_value;
        }
        if (sub.max_items) {
          subItem.max_items = sub.max_items;
        }
        return subItem;
      });
    }

    return item;
  });

  return JSON.stringify(simplified, null, 2);
}

/**
 * Parses JSON schema representation into TableColumn[]
 */
export function parseJsonToColumns(jsonStr: string, existingColumns: TableColumn[] = []): TableColumn[] {
  const parsed = JSON.parse(jsonStr);
  let rawList: any[] = [];

  if (Array.isArray(parsed)) {
    rawList = parsed;
  } else if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed.columns)) {
      rawList = parsed.columns;
    } else if (Array.isArray(parsed.fields)) {
      rawList = parsed.fields;
    } else if (Array.isArray(parsed.schema)) {
      rawList = parsed.schema;
    } else {
      throw new Error('JSON root must be an array of column definitions, e.g. [ { "key": "email", ... } ]');
    }
  } else {
    throw new Error('JSON root must be an array of column definitions');
  }

  const existingMap = new Map<string, TableColumn>();
  existingColumns.forEach((c) => existingMap.set(c.key.toLowerCase(), c));

  return rawList.map((item, index) => {
    const rawKey =
      typeof item.key === 'string'
        ? item.key
        : typeof item.name === 'string'
        ? item.name
        : `field_${index + 1}`;
    const key = rawKey
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const name =
      typeof item.name === 'string' && item.name.trim()
        ? item.name.trim()
        : key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

    const dataType = (
      typeof item.data_type === 'string'
        ? item.data_type
        : typeof item.type === 'string'
        ? item.type
        : 'text'
    ).toLowerCase();

    const isRequired = Boolean(item.is_required || item.required);
    const isNullable = item.is_nullable !== undefined ? Boolean(item.is_nullable) : !isRequired;

    const existing = existingMap.get(key);
    const id = existing
      ? existing.id
      : `temp_${Date.now()}_${index}_${Math.random().toString(36).substring(2, 6)}`;
    const tableId = existing?.table_id || '';

    // Validation rules
    const validationRules: Record<string, any> = {
      ...(existing?.validation_rules || {}),
      ...(item.validation_rules || {}),
    };

    if (item.sample_value !== undefined && item.sample_value !== '') {
      validationRules.sample_value = String(item.sample_value);
    }
    if (item.max_items !== undefined && item.max_items !== '') {
      const parsedMax = parseInt(String(item.max_items), 10);
      if (!isNaN(parsedMax) && parsedMax > 0) {
        validationRules.max_items = parsedMax;
      }
    }

    if (Array.isArray(item.subfields)) {
      validationRules.subfields = item.subfields.map((sub: any, sIdx: number) => {
        const sKey = (sub.key || sub.name || `sub_${sIdx + 1}`)
          .toLowerCase()
          .replace(/[^a-z0-9_]+/g, '_')
          .replace(/^_+|_+$/g, '');
        const sName =
          (sub.name || '').trim() ||
          sKey.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        return {
          id: sub.id || `sub_${Date.now()}_${sIdx}_${Math.random().toString(36).substring(2, 6)}`,
          key: sKey,
          name: sName,
          data_type: (sub.data_type || sub.type || 'text').toLowerCase(),
          is_required: Boolean(sub.is_required || sub.required),
          sample_value: sub.sample_value ? String(sub.sample_value) : '',
          max_items: sub.max_items ? parseInt(String(sub.max_items), 10) : undefined,
        };
      });
    }

    return {
      id,
      table_id: tableId,
      name,
      key,
      data_type: dataType,
      is_required: isRequired,
      is_nullable: isNullable,
      default_value: item.default_value,
      position: index,
      width: existing?.width || 180,
      is_visible: existing?.is_visible !== undefined ? existing.is_visible : true,
      validation_rules: validationRules,
      created_at: existing?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  });
}

/**
 * Validates JSON syntax AND structural schema conformity:
 * - Checks JSON syntax errors (with line/column detection)
 * - Checks top-level data types
 * - Enforces scalar-only subfield types (subfields cannot be of type 'object' or 'array')
 * - Prevents deep multi-level subfield nesting
 * - Validates key uniqueness and alphanumeric format
 * - Checks constraint compatibility (e.g. max_items only on arrays)
 */
export function validateJsonSchema(jsonStr: string): JsonValidationResult {
  if (!jsonStr.trim()) {
    return {
      isValid: false,
      isSyntaxError: true,
      syntaxError: 'JSON schema definition is empty.',
      schemaErrors: [],
    };
  }

  let parsed: any;
  try {
    parsed = JSON.parse(jsonStr);
  } catch (err: any) {
    const rawMsg = err.message || 'Invalid JSON syntax';
    let line: number | undefined;
    let column: number | undefined;

    const lineColMatch = rawMsg.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (lineColMatch) {
      line = parseInt(lineColMatch[1], 10);
      column = parseInt(lineColMatch[2], 10);
    } else {
      const posMatch = rawMsg.match(/position\s+(\d+)/i);
      if (posMatch) {
        const pos = parseInt(posMatch[1], 10);
        const sliced = jsonStr.slice(0, pos);
        const lines = sliced.split('\n');
        line = lines.length;
        column = lines[lines.length - 1].length + 1;
      }
    }

    return {
      isValid: false,
      isSyntaxError: true,
      syntaxError: rawMsg,
      line,
      column,
      schemaErrors: [],
    };
  }

  const schemaErrors: SchemaError[] = [];

  // Structural root check
  let rawList: any[] = [];
  if (Array.isArray(parsed)) {
    rawList = parsed;
  } else if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed.columns)) {
      rawList = parsed.columns;
    } else if (Array.isArray(parsed.fields)) {
      rawList = parsed.fields;
    } else if (Array.isArray(parsed.schema)) {
      rawList = parsed.schema;
    } else {
      schemaErrors.push({
        path: 'root',
        message: 'Root must be an array of field definitions, e.g. [ { "key": "email", "data_type": "email" }, ... ]',
      });
      return {
        isValid: false,
        isSyntaxError: false,
        schemaErrors,
      };
    }
  } else {
    schemaErrors.push({
      path: 'root',
      message: 'Root JSON value must be an array of field objects.',
    });
    return {
      isValid: false,
      isSyntaxError: false,
      schemaErrors,
    };
  }

  const seenKeys = new Set<string>();

  for (let i = 0; i < rawList.length; i++) {
    const item = rawList[i];
    const fieldIndexLabel = `field[${i + 1}]`;

    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      schemaErrors.push({
        path: fieldIndexLabel,
        message: `Field item at position ${i + 1} must be a JSON object with at least "key" and "data_type".`,
      });
      continue;
    }

    // 1. Key validation
    const rawKey = item.key !== undefined ? String(item.key).trim() : '';
    const fieldKey = rawKey.toLowerCase();

    if (!rawKey) {
      schemaErrors.push({
        path: fieldIndexLabel,
        message: `Field at position ${i + 1} is missing a required "key" property.`,
      });
    } else {
      if (!/^[a-z0-9_]+$/.test(fieldKey)) {
        schemaErrors.push({
          path: fieldKey,
          message: `Key "${rawKey}" contains invalid characters. Keys must contain only lowercase letters, digits, and underscores.`,
        });
      }

      if (seenKeys.has(fieldKey)) {
        schemaErrors.push({
          path: fieldKey,
          message: `Duplicate field key "${fieldKey}". Column keys must be unique.`,
        });
      } else {
        seenKeys.add(fieldKey);
      }
    }

    const currentFieldPath = fieldKey || fieldIndexLabel;

    // 2. Data Type validation
    const rawType = (item.data_type || item.type || '').toString().toLowerCase().trim();
    if (!rawType) {
      schemaErrors.push({
        path: currentFieldPath,
        message: `Field "${currentFieldPath}" is missing a "data_type". Supported types: text, number, boolean, email, date, uuid, object, array, file.`,
      });
    } else if (!VALID_TOP_LEVEL_TYPES.has(rawType)) {
      schemaErrors.push({
        path: currentFieldPath,
        message: `Invalid data_type "${rawType}". Supported top-level types: text, number, boolean, email, date, uuid, object, array, file.`,
      });
    }

    // 3. Array constraint validation (max_items)
    if (item.max_items !== undefined && item.max_items !== null && item.max_items !== '') {
      if (rawType !== 'array') {
        schemaErrors.push({
          path: `${currentFieldPath}.max_items`,
          message: `"max_items" constraint is only permitted on "array" data types (found on "${rawType || 'unspecified'}").`,
        });
      } else {
        const parsedMax = Number(item.max_items);
        if (!Number.isInteger(parsedMax) || parsedMax <= 0) {
          schemaErrors.push({
            path: `${currentFieldPath}.max_items`,
            message: `"max_items" must be a positive integer greater than 0 (received ${item.max_items}).`,
          });
        }
      }
    }

    // 4. Subfield validation (nested properties)
    const hasSubfields = item.subfields !== undefined && item.subfields !== null;
    if (hasSubfields) {
      if (rawType !== 'object' && rawType !== 'array') {
        schemaErrors.push({
          path: `${currentFieldPath}.subfields`,
          message: `Subfields can only be configured for "object" or "array" types (found on "${rawType || 'unspecified'}").`,
        });
      } else if (!Array.isArray(item.subfields)) {
        schemaErrors.push({
          path: `${currentFieldPath}.subfields`,
          message: `"subfields" must be an array of nested field definitions.`,
        });
      } else {
        const seenSubKeys = new Set<string>();

        for (let sIdx = 0; sIdx < item.subfields.length; sIdx++) {
          const sub = item.subfields[sIdx];
          const subIndexLabel = `${currentFieldPath}.subfields[${sIdx + 1}]`;

          if (!sub || typeof sub !== 'object' || Array.isArray(sub)) {
            schemaErrors.push({
              path: subIndexLabel,
              message: `Subfield item at index ${sIdx + 1} must be an object.`,
            });
            continue;
          }

          const rawSubKey = sub.key !== undefined ? String(sub.key).trim() : '';
          const subKey = rawSubKey.toLowerCase();
          const subFieldPath = subKey ? `${currentFieldPath}.${subKey}` : subIndexLabel;

          if (!rawSubKey) {
            schemaErrors.push({
              path: subIndexLabel,
              message: `Subfield at position ${sIdx + 1} under "${currentFieldPath}" is missing a "key".`,
            });
          } else {
            if (!/^[a-z0-9_]+$/.test(subKey)) {
              schemaErrors.push({
                path: subFieldPath,
                message: `Subfield key "${rawSubKey}" contains invalid characters. Keys must contain only lowercase letters, digits, and underscores.`,
              });
            }

            if (seenSubKeys.has(subKey)) {
              schemaErrors.push({
                path: subFieldPath,
                message: `Duplicate subfield key "${subKey}" under "${currentFieldPath}". Subfield keys must be unique within their parent.`,
              });
            } else {
              seenSubKeys.add(subKey);
            }
          }

          // Subfield data_type check
          const rawSubType = (sub.data_type || sub.type || '').toString().toLowerCase().trim();
          if (!rawSubType) {
            schemaErrors.push({
              path: subFieldPath,
              message: `Subfield "${subFieldPath}" is missing a "data_type".`,
            });
          } else if (rawSubType === 'object' || rawSubType === 'array') {
            // Nested object/array in subfields is forbidden
            schemaErrors.push({
              path: subFieldPath,
              message: `Subfield "${subFieldPath}" cannot be of type "${rawSubType}". Nested "object" or "array" types inside subfields are not supported (single-level nesting only).`,
            });
          } else if (!VALID_SUBFIELD_TYPES.has(rawSubType)) {
            schemaErrors.push({
              path: subFieldPath,
              message: `Subfield "${subFieldPath}" has unsupported data_type "${rawSubType}". Allowed subfield types: text, number, boolean, email, date, uuid, file.`,
            });
          }

          // Deep nesting check
          if (sub.subfields && Array.isArray(sub.subfields) && sub.subfields.length > 0) {
            schemaErrors.push({
              path: `${subFieldPath}.subfields`,
              message: `Subfield "${subFieldPath}" contains nested subfields. Deep multi-level nesting is not supported.`,
            });
          }
        }
      }
    }
  }

  return {
    isValid: schemaErrors.length === 0,
    isSyntaxError: false,
    schemaErrors,
    fieldCount: rawList.length,
  };
}

/**
 * Tokenizes a single line of JSON for syntax highlighting
 */
function tokenizeJsonLine(line: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  const regex =
    /("(?:[^"\\]|\\.)*")(\s*:)|("(?:[^"\\]|\\.)*")|(\b(?:true|false|null)\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],:])/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: line.slice(lastIndex, match.index), type: 'plain' });
    }

    if (match[1]) {
      // Key followed by colon
      tokens.push({ text: match[1], type: 'key' });
      if (match[2]) {
        tokens.push({ text: match[2], type: 'punctuation' });
      }
    } else if (match[3]) {
      // String value
      tokens.push({ text: match[3], type: 'string' });
    } else if (match[4]) {
      const val = match[4];
      tokens.push({ text: val, type: val === 'null' ? 'null' : 'boolean' });
    } else if (match[5]) {
      tokens.push({ text: match[5], type: 'number' });
    } else if (match[6]) {
      tokens.push({ text: match[6], type: 'punctuation' });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < line.length) {
    tokens.push({ text: line.slice(lastIndex), type: 'plain' });
  }

  return tokens;
}

function getTokenClass(type: JsonToken['type']): string {
  switch (type) {
    case 'key':
      return 'text-sky-300 font-medium';
    case 'string':
      return 'text-emerald-300';
    case 'number':
      return 'text-amber-300';
    case 'boolean':
      return 'text-purple-300 font-medium';
    case 'null':
      return 'text-zinc-500 italic';
    case 'punctuation':
      return 'text-zinc-400';
    case 'plain':
    default:
      return 'text-zinc-200';
  }
}

export const AdvancedJsonSchema: React.FC<AdvancedJsonSchemaProps> = ({
  columns,
  canEdit,
  onApplyColumns,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [jsonText, setJsonText] = useState(() => serializeColumnsToJson(columns));
  const [copied, setCopied] = useState(false);

  const isManuallyEditedRef = useRef(false);
  const gutterRef = useRef<HTMLDivElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync JSON representation whenever columns change externally (unless user actively typed in editor)
  useEffect(() => {
    if (!isManuallyEditedRef.current) {
      setJsonText(serializeColumnsToJson(columns));
    }
  }, [columns]);

  // Syntax and structural validation
  const validation = useMemo(() => validateJsonSchema(jsonText), [jsonText]);

  const isDirty = useMemo(() => {
    try {
      return (
        JSON.stringify(JSON.parse(jsonText)) !==
        JSON.stringify(JSON.parse(serializeColumnsToJson(columns)))
      );
    } catch {
      return true;
    }
  }, [jsonText, columns]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(jsonText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = jsonText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonText(formatted);
    } catch {
      // Ignore formatting on invalid JSON
    }
  };

  const handleSyncFromTable = () => {
    isManuallyEditedRef.current = false;
    setJsonText(serializeColumnsToJson(columns));
  };

  const handleApply = () => {
    if (!validation.isValid || validation.schemaErrors.length > 0 || !onApplyColumns) return;
    try {
      const parsedCols = parseJsonToColumns(jsonText, columns);
      isManuallyEditedRef.current = false;
      onApplyColumns(parsedCols);
    } catch (err) {
      console.error('Failed to apply schema:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const val = textarea.value;
      const nextVal = val.substring(0, start) + '  ' + val.substring(end);
      isManuallyEditedRef.current = true;
      setJsonText(nextVal);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  const handleScroll = () => {
    if (textareaRef.current) {
      const { scrollTop, scrollLeft } = textareaRef.current;
      if (preRef.current) {
        preRef.current.scrollTop = scrollTop;
        preRef.current.scrollLeft = scrollLeft;
      }
      if (gutterRef.current) {
        gutterRef.current.scrollTop = scrollTop;
      }
    }
  };

  const lines = jsonText.split('\n');

  return (
    <div className="rounded-lg border border-cf-border bg-cf-surface overflow-hidden shadow-xs">
      {/* Collapsible Disclosure Header */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        className="w-full flex items-center justify-between p-3.5 bg-cf-surface hover:bg-cf-surfaceHover transition-colors cursor-pointer text-left select-none"
      >
        <div className="flex items-center gap-2.5">
          {isOpen ? (
            <ChevronDown size={15} className="text-cf-textMuted" />
          ) : (
            <ChevronRight size={15} className="text-cf-textMuted" />
          )}
          <FileCode size={16} className="text-cf-orange shrink-0" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-cf-textPrimary">Advanced mode (JSON)</span>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-textMuted font-mono">
              Live Syntax &amp; Schema Validation
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {/* Collapsed summary pill */}
          {validation.isValid ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <CheckCircle2 size={11} />
              <span>Valid Schema ({columns.length} fields)</span>
            </span>
          ) : validation.isSyntaxError ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono bg-rose-500/10 text-rose-400 border border-rose-500/20">
              <AlertCircle size={11} />
              <span>JSON Syntax Error</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono bg-rose-500/10 text-rose-400 border border-rose-500/20">
              <AlertCircle size={11} />
              <span>
                Invalid Schema ({validation.schemaErrors.length}{' '}
                {validation.schemaErrors.length === 1 ? 'error' : 'errors'})
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Collapsed Content Body */}
      {isOpen && (
        <div className="border-t border-cf-border bg-cf-bg/40">
          {/* Action Toolbar */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-cf-border bg-cf-surface flex-wrap gap-2">
            {/* Syntax and Schema Validation Status Indicator */}
            <div className="flex items-center gap-2">
              {validation.isValid ? (
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-xs font-mono">
                  <CheckCircle2 size={13} className="shrink-0" />
                  <span>
                    Valid Schema
                    {validation.fieldCount !== undefined ? ` · ${validation.fieldCount} fields` : ''}
                  </span>
                </div>
              ) : validation.isSyntaxError ? (
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-mono">
                  <AlertCircle size={13} className="shrink-0" />
                  <span>
                    JSON Syntax Error
                    {validation.line ? ` (Line ${validation.line}, Col ${validation.column})` : ''}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-mono">
                  <AlertCircle size={13} className="shrink-0" />
                  <span>
                    Invalid Schema ({validation.schemaErrors.length}{' '}
                    {validation.schemaErrors.length === 1 ? 'issue' : 'issues'})
                  </span>
                </div>
              )}
            </div>

            {/* Control Actions */}
            <div className="flex items-center gap-2">
              {/* Format JSON */}
              <Button
                variant="secondary"
                size="xs"
                onClick={handleFormat}
                disabled={validation.isSyntaxError}
                leftIcon={<Sparkles size={12} className="text-cf-orange" />}
                title="Format JSON with 2-space indentation"
              >
                Format
              </Button>

              {/* Copy Code */}
              <Button
                variant="secondary"
                size="xs"
                onClick={handleCopy}
                leftIcon={copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                title="Copy JSON to clipboard"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>

              {/* Sync from Table if modified */}
              {isDirty && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleSyncFromTable}
                  leftIcon={<RefreshCw size={12} />}
                  title="Reset JSON to current table schema"
                >
                  Reset
                </Button>
              )}

              {/* Apply to Table (Disabled on invalid syntax or invalid schema) */}
              {canEdit && (
                <Button
                  variant="primary"
                  size="xs"
                  onClick={handleApply}
                  disabled={!validation.isValid || validation.schemaErrors.length > 0 || !isDirty}
                  leftIcon={<ArrowUpFromLine size={12} />}
                  title={
                    !validation.isValid
                      ? 'Fix schema errors to apply changes'
                      : 'Apply JSON schema definitions to the table grid'
                  }
                >
                  Apply to Table
                </Button>
              )}
            </div>
          </div>

          {/* JSON Syntax Error Banner */}
          {validation.isSyntaxError && validation.syntaxError && (
            <div className="px-3.5 py-2.5 bg-rose-500/10 border-b border-rose-500/25 text-rose-400 text-xs font-mono flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5 text-rose-400" />
              <div>
                <span className="font-semibold">JSON Syntax Error: </span>
                <span className="text-rose-300">{validation.syntaxError}</span>
              </div>
            </div>
          )}

          {/* Schema Violations Detailed Banner */}
          {!validation.isSyntaxError && validation.schemaErrors.length > 0 && (
            <div className="px-3.5 py-2.5 bg-rose-500/10 border-b border-rose-500/25 text-rose-400 text-xs font-mono">
              <div className="flex items-center gap-2 font-semibold text-rose-400 mb-1.5">
                <AlertCircle size={14} className="shrink-0 text-rose-400" />
                <span>
                  Invalid Schema ({validation.schemaErrors.length}{' '}
                  {validation.schemaErrors.length === 1 ? 'issue' : 'issues'} found):
                </span>
              </div>
              <ul className="space-y-1 pl-6 list-disc text-rose-300 text-[11px] leading-relaxed max-h-36 overflow-y-auto">
                {validation.schemaErrors.map((err, idx) => (
                  <li key={idx}>
                    <code className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 font-mono text-[10px] mr-1.5 font-semibold">
                      {err.path}
                    </code>
                    <span>{err.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unified Syntax-Highlighted Interactive Code Editor */}
          <div className="relative flex bg-[#0d0d0d] text-zinc-200 font-mono text-xs border-b border-cf-border h-[380px] overflow-hidden">
            {/* Line Numbers Gutter */}
            <div
              ref={gutterRef}
              className="w-12 py-3 pr-3 text-right bg-[#090909] text-zinc-600 select-none pointer-events-none border-r border-zinc-800/80 font-mono text-[11px] leading-5 overflow-hidden shrink-0"
            >
              {lines.map((_, idx) => (
                <div key={idx} className="h-5 leading-5">
                  {idx + 1}
                </div>
              ))}
            </div>

            {/* Code Canvas (Syntax Highlight Layer + Live Input Layer) */}
            <div className="relative flex-1 overflow-hidden h-full">
              {/* Syntax Highlighted Render Layer */}
              <pre
                ref={preRef}
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none p-3 m-0 font-mono text-xs leading-5 whitespace-pre overflow-hidden select-none border-0"
                style={{ tabSize: 2, MozTabSize: 2 }}
              >
                {lines.map((line, idx) => {
                  const tokens = tokenizeJsonLine(line);
                  return (
                    <div key={idx} className="h-5 leading-5 whitespace-pre">
                      {tokens.length === 0 ? (
                        '\u00A0'
                      ) : (
                        tokens.map((token, tIdx) => (
                          <span key={tIdx} className={getTokenClass(token.type)}>
                            {token.text}
                          </span>
                        ))
                      )}
                    </div>
                  );
                })}
              </pre>

              {/* Live Interactive Textarea Overlay */}
              <textarea
                ref={textareaRef}
                value={jsonText}
                onChange={(e) => {
                  isManuallyEditedRef.current = true;
                  setJsonText(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                spellCheck={false}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                wrap="off"
                placeholder="Paste or write JSON schema definition here..."
                className="absolute inset-0 w-full h-full p-3 m-0 bg-transparent text-transparent caret-white font-mono text-xs leading-5 whitespace-pre resize-none focus:outline-none overflow-auto border-0 selection:bg-sky-500/30 selection:text-transparent"
                style={{ tabSize: 2, MozTabSize: 2 }}
              />
            </div>
          </div>

          {/* Footer Guide and Helper Notes */}
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-cf-bg/80 text-[11px] text-cf-textMuted font-mono flex-wrap gap-2">
            <span>
              Subfields can only be scalar types (<code>text</code>, <code>number</code>, <code>boolean</code>, <code>email</code>, <code>date</code>, <code>uuid</code>, <code>file</code>). Nested <code>object</code>/<code>array</code> subfields are prohibited.
            </span>
            {canEdit && isDirty && validation.isValid && (
              <span className="text-cf-orange font-semibold">
                Unapplied JSON changes. Click &quot;Apply to Table&quot; to reflect in the grid.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
